import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  X,
  Search,
  User,
  Trash2,
  CreditCard,
  Banknote,
  Check,
  CheckSquare,
  Mail,
  Tag,
  Loader2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { Elements } from '@stripe/react-stripe-js';

import { Button } from '../ui/Button';
import { useToast } from '../ui/Toast';
import { useCurrentCompany } from '../../hooks/useCurrentCompany';
import { useOutstandingPayments, type FamilyGroup, type SubscriptionWithAmountDue } from '../../hooks/useOutstandingPayments';
import { useDiscountCodes } from '../../hooks/useDiscountCodes';
import { useRecordPayment } from '../../hooks/useRecordPayment';
import { useCreatePaymentIntent } from '../../hooks/useCreatePaymentIntent';
import { useLockScroll } from '../../hooks/useLockScroll';
import { useStripeContext } from '../../providers/StripeProvider';
import { CardPaymentForm } from './CardPaymentForm';

interface AddPaymentDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

/** Cart item - either balance (enrollment) or miscellaneous (name + price) */
interface CartItem {
  id: string;
  enrollmentId?: string; // present = balance item, absent = added item
  label: string;
  amount: number;
  quantity: number;
  billingDate?: string;
}

/**
 * Add Payment Drawer - Record payments for outstanding balances and sell miscellaneous items.
 * Member flow: select member, check balance items to pay, add misc items (name + price).
 * Guest flow: UI ready, backend coming soon.
 */
export function AddPaymentDrawer({ isOpen, onClose }: AddPaymentDrawerProps) {
  const queryClient = useQueryClient();
  const { data: companyId } = useCurrentCompany();
  useLockScroll(isOpen);
  const { showToast, ToastContainer } = useToast();
  const { data: familyGroups = [], isLoading: isLoadingFamilies } = useOutstandingPayments({
    companyId: companyId ?? null,
    enabled: !!companyId && isOpen,
    includeZeroBalance: true, // Show all members, not just those with balance due
  });
  const { data: discountCodes = [] } = useDiscountCodes();
  const recordPaymentMutation = useRecordPayment();
  const createPaymentIntentMutation = useCreatePaymentIntent();
  const { stripePromise } = useStripeContext();
  const cardIntentEnrollmentIdRef = useRef<string | null>(null);

  const [customerType, setCustomerType] = useState<'member' | 'guest'>('member');
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [showMemberSuggestions, setShowMemberSuggestions] = useState(false);
  const [selectedFamily, setSelectedFamily] = useState<FamilyGroup | null>(null);
  const [selectedBalanceIds, setSelectedBalanceIds] = useState<Set<string>>(new Set());
  const [balanceQuantities, setBalanceQuantities] = useState<Record<string, number>>({});
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'cash' | 'check'>('cash');
  const [useCardOnFile, setUseCardOnFile] = useState(true);
  const [amountReceived, setAmountReceived] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [addedCartItems, setAddedCartItems] = useState<CartItem[]>([]);
  const [showCustomPriceInput, setShowCustomPriceInput] = useState(false);
  const [customItemName, setCustomItemName] = useState('');
  const [customItemPrice, setCustomItemPrice] = useState('');

  const filteredMemberSuggestions = useMemo(() => {
    if (!memberSearchQuery.trim()) return familyGroups;
    const q = memberSearchQuery.trim().toLowerCase();
    return familyGroups.filter(
      (fg) =>
        fg.family_lead_name.toLowerCase().includes(q) ||
        fg.family_lead_last_name.toLowerCase().includes(q) ||
        fg.subscriptions.some((s) => s.student_name.toLowerCase().includes(q)),
    );
  }, [familyGroups, memberSearchQuery]);

  const hasSelectedMember = selectedFamily !== null;

  const resetForm = () => {
    setSelectedFamily(null);
    setSelectedBalanceIds(new Set());
    setBalanceQuantities({});
    setMemberSearchQuery('');
    setAmountReceived('');
    setShowSuccess(false);
    setAddedCartItems([]);
    setItemSearchQuery('');
    setShowCustomPriceInput(false);
    setCustomItemName('');
    setCustomItemPrice('');
    cardIntentEnrollmentIdRef.current = null;
    createPaymentIntentMutation.reset();
  };

  const handleSelectFamily = (fg: FamilyGroup) => {
    setSelectedFamily(fg);
    setSelectedBalanceIds(new Set());
    setBalanceQuantities({});
    setMemberSearchQuery(fg.family_lead_name);
    setShowMemberSuggestions(false);
  };

  const outstandingItemsWithBalance =
    selectedFamily?.subscriptions.filter((s) => s.outstanding_balance > 0) ?? [];

  const balanceCartItems = useMemo(() => {
    if (!selectedFamily) return [];
    return outstandingItemsWithBalance
      .filter((s) => selectedBalanceIds.has(s.enrollment_id))
      .map((s) => ({
        id: s.enrollment_id,
        enrollmentId: s.enrollment_id,
        label: `${s.student_name} - ${s.subscription_name}`,
        amount: s.outstanding_balance,
        quantity: balanceQuantities[s.enrollment_id] ?? 1,
        billingDate: s.billing_date,
      }));
  }, [selectedFamily, selectedBalanceIds, outstandingItemsWithBalance, balanceQuantities]);

  const cartItems = [...balanceCartItems, ...addedCartItems];
  const hasAddedItems = addedCartItems.length > 0;
  const totalDue = cartItems.reduce((sum, i) => sum + i.amount * i.quantity, 0);

  // Card payments support exactly one balance item (no misc items)
  const canUseCard = balanceCartItems.length === 1 && !hasAddedItems;
  const singleEnrollmentId = canUseCard ? balanceCartItems[0].enrollmentId : null;

  // Create payment intent when Card is selected and exactly one balance item
  useEffect(() => {
    if (paymentMethod !== 'card' || !canUseCard || !singleEnrollmentId || !companyId) return;
    if (cardIntentEnrollmentIdRef.current === singleEnrollmentId) return;
    if (createPaymentIntentMutation.data?.client_secret && createPaymentIntentMutation.isSuccess) return;
    if (createPaymentIntentMutation.isPending) return;

    cardIntentEnrollmentIdRef.current = singleEnrollmentId;
    createPaymentIntentMutation.mutate({ enrollmentId: singleEnrollmentId });
  }, [paymentMethod, canUseCard, singleEnrollmentId, companyId]);

  // Reset intent when selection changes (different item or no longer single-item)
  useEffect(() => {
    if (!canUseCard) {
      cardIntentEnrollmentIdRef.current = null;
      createPaymentIntentMutation.reset();
    } else if (singleEnrollmentId && cardIntentEnrollmentIdRef.current !== singleEnrollmentId) {
      cardIntentEnrollmentIdRef.current = null;
      createPaymentIntentMutation.reset();
    }
  }, [canUseCard, singleEnrollmentId]);

  const amountNum = parseFloat(amountReceived.replace(/[^0-9.]/g, '')) || 0;
  const changeDue = Math.max(0, amountNum - totalDue);

  const toggleBalanceSelection = (sub: SubscriptionWithAmountDue) => {
    setSelectedBalanceIds((prev) => {
      const next = new Set(prev);
      if (next.has(sub.enrollment_id)) {
        next.delete(sub.enrollment_id);
        setBalanceQuantities((q) => {
          const n = { ...q };
          delete n[sub.enrollment_id];
          return n;
        });
      } else {
        next.add(sub.enrollment_id);
      }
      return next;
    });
  };

  const handleQuantityChange = (item: CartItem, delta: number) => {
    const newQty = Math.max(1, item.quantity + delta);
    if (item.enrollmentId) {
      setBalanceQuantities((prev) => ({
        ...prev,
        [item.enrollmentId!]: newQty,
      }));
    } else {
      setAddedCartItems((prev) =>
        prev.map((i) =>
          i.id === item.id ? { ...i, quantity: newQty } : i,
        ),
      );
    }
  };

  const handleAddMiscItem = (name: string, price: number) => {
    if (!name.trim()) return;
    const p = Number.isNaN(price) ? 0 : price;
    if (p <= 0) {
      showToast('Enter a valid price', 'error');
      return;
    }
    const existing = addedCartItems.find(
      (i) => !i.enrollmentId && i.label.toLowerCase() === name.trim().toLowerCase(),
    );
    if (existing) {
      setAddedCartItems((prev) =>
        prev.map((i) =>
          i.id === existing.id ? { ...i, quantity: i.quantity + 1 } : i,
        ),
      );
    } else {
      setAddedCartItems((prev) => [
        ...prev,
        {
          id: `custom-${Date.now()}-${name}`,
          label: name.trim(),
          amount: p,
          quantity: 1,
        },
      ]);
    }
    setShowCustomPriceInput(false);
    setCustomItemName('');
    setCustomItemPrice('');
    setItemSearchQuery('');
  };

  const handleRemoveCartItem = (item: CartItem) => {
    if (item.enrollmentId) {
      setSelectedBalanceIds((prev) => {
        const next = new Set(prev);
        next.delete(item.enrollmentId!);
        return next;
      });
      setBalanceQuantities((prev) => {
        const n = { ...prev };
        delete n[item.enrollmentId!];
        return n;
      });
    } else {
      setAddedCartItems((prev) => prev.filter((i) => i.id !== item.id));
    }
  };

  useEffect(() => {
    if (isOpen) {
      resetForm();
      setCustomerType('member');
      setPaymentMethod('cash');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      <ToastContainer />
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 z-40 transition-opacity"
        onClick={onClose}
        aria-hidden
      />

      {/* Modal - centered pop-up */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-label="Add payment"
        onClick={onClose}
      >
        <div
          className="w-full max-w-xl max-h-[90vh] bg-[#1a1818] border border-[#504A4A] rounded-xl shadow-2xl flex flex-col animate-in zoom-in-95 fade-in-0 duration-200"
          onClick={(e) => e.stopPropagation()}
        >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#504A4A] shrink-0">
          <h2 className="text-lg font-semibold text-[#EBFFEE]">Add Payment</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-white/5 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content - scrollable */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6 min-h-0">
          {/* SECTION 1: Customer Attribution */}
          <section>
            <h3 className="text-sm font-medium text-[#A9A3A3] mb-3">Customer</h3>
            <div className="flex rounded-lg bg-[#221F1F] p-1 border border-[#504A4A]">
              <button
                type="button"
                onClick={() => {
                  setCustomerType('member');
                  setSelectedFamily(null);
                  setSelectedBalanceIds(new Set());
                  setBalanceQuantities({});
                  setMemberSearchQuery('');
                }}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors border-2 ${
                  customerType === 'member'
                    ? 'border-white bg-white/5 text-[#EBFFEE]'
                    : 'border-transparent text-[#A9A3A3] hover:text-[#EBFFEE]'
                }`}
              >
                Member
              </button>
              <button
                type="button"
                onClick={() => {
                  setCustomerType('guest');
                  setSelectedFamily(null);
                  setSelectedBalanceIds(new Set());
                  setBalanceQuantities({});
                  setMemberSearchQuery('');
                }}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors border-2 ${
                  customerType === 'guest'
                    ? 'border-white bg-white/5 text-[#EBFFEE]'
                    : 'border-transparent text-[#A9A3A3] hover:text-[#EBFFEE]'
                }`}
              >
                Guest
              </button>
            </div>

            {customerType === 'member' ? (
              <div className="mt-3 space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A9A3A3]" />
                  <input
                    type="text"
                    placeholder="Search members..."
                    value={memberSearchQuery}
                    onChange={(e) => setMemberSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 bg-[#221F1F] border border-[#504A4A] rounded-lg text-[#EBFFEE] placeholder-[#A9A3A3] text-sm focus:outline-none focus:border-green-700"
                    onFocus={() => setShowMemberSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowMemberSuggestions(false), 150)}
                  />
                  {showMemberSuggestions && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-[#221F1F] border border-[#504A4A] rounded-lg shadow-xl z-10 overflow-hidden max-h-60 overflow-y-auto">
                      {isLoadingFamilies ? (
                        <div className="px-4 py-6 text-center text-[#A9A3A3] text-sm">
                          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                          Loading...
                        </div>
                      ) : filteredMemberSuggestions.length > 0 ? (
                        filteredMemberSuggestions.map((fg) => (
                          <button
                            key={fg.billing_account_id}
                            type="button"
                            className="w-full px-4 py-3 text-left hover:bg-white/5 flex items-center justify-between text-sm border-b border-[#504A4A]/50 last:border-b-0"
                            onClick={() => handleSelectFamily(fg)}
                          >
                            <span className="text-[#EBFFEE]">{fg.family_lead_name}</span>
                            <span className={fg.current_due > 0 ? 'text-amber-400' : 'text-[#A9A3A3]'}>
                              {fg.current_due > 0 ? `$${fg.current_due.toFixed(2)} due` : 'Paid up'}
                            </span>
                          </button>
                        ))
                      ) : (
                        <div className="px-4 py-4 text-sm text-[#A9A3A3]">
                          No members match your search
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {hasSelectedMember && selectedFamily && (
                  <>
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-700/20 text-green-700 text-sm font-medium">
                        <User className="w-4 h-4" />
                        {selectedFamily.family_lead_name}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedFamily(null);
                          setSelectedBalanceIds(new Set());
                          setBalanceQuantities({});
                          setMemberSearchQuery('');
                        }}
                        className="text-xs text-[#A9A3A3] hover:text-[#EBFFEE] underline"
                      >
                        Change
                      </button>
                    </div>
                    <div className="space-y-2">
                      <p className="text-xs text-[#A9A3A3]">
                        Outstanding Balance — check items you are paying
                      </p>
                      {outstandingItemsWithBalance.length > 0 ? (
                        outstandingItemsWithBalance.map((sub) => (
                          <label
                            key={sub.enrollment_id}
                            className="flex items-center gap-3 p-3 rounded-lg bg-[#221F1F] border border-[#504A4A] cursor-pointer hover:border-[#504A4A]/80"
                          >
                            <input
                              type="checkbox"
                              checked={selectedBalanceIds.has(sub.enrollment_id)}
                              onChange={() => toggleBalanceSelection(sub)}
                              className="w-4 h-4 rounded border-[#504A4A] text-green-700 bg-[#111010] focus:ring-green-700"
                            />
                            <span className="flex-1 text-sm text-[#EBFFEE]">
                              {sub.student_name} – {sub.subscription_name}
                            </span>
                            <span className="text-sm font-medium text-amber-400">
                              ${sub.outstanding_balance.toFixed(2)}
                            </span>
                          </label>
                        ))
                      ) : (
                        <p className="text-sm text-[#A9A3A3] py-2">No outstanding balance</p>
                      )}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="mt-3">
                <input
                  type="text"
                  placeholder="Guest Name (Optional)"
                  className="w-full px-4 py-2.5 bg-[#221F1F] border border-[#504A4A] rounded-lg text-[#EBFFEE] placeholder-[#A9A3A3] text-sm focus:outline-none focus:border-green-700"
                />
              </div>
            )}
          </section>

          {/* SECTION 2: Transaction Building */}
          <section>
            <h3 className="text-sm font-medium text-[#A9A3A3] mb-3">Items</h3>
            <div className="mb-4">
              <input
                type="text"
                placeholder="Add item (type name, press Enter)"
                value={itemSearchQuery}
                onChange={(e) => setItemSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && itemSearchQuery.trim()) {
                    e.preventDefault();
                    setCustomItemName(itemSearchQuery.trim());
                    setShowCustomPriceInput(true);
                  }
                }}
                className="w-full px-4 py-2.5 bg-[#221F1F] border border-[#504A4A] rounded-lg text-[#EBFFEE] placeholder-[#A9A3A3] text-sm focus:outline-none focus:border-green-700"
              />
            </div>

            {showCustomPriceInput && (
              <div className="mb-4 p-4 rounded-lg bg-[#221F1F] border border-[#504A4A] space-y-2">
                <p className="text-sm text-[#EBFFEE]">Add miscellaneous item: {customItemName}</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Price (e.g. 15.00)"
                    value={customItemPrice}
                    onChange={(e) => setCustomItemPrice(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const price = parseFloat(customItemPrice.replace(/[^0-9.]/g, ''));
                        handleAddMiscItem(customItemName, price);
                      }
                    }}
                    className="flex-1 px-4 py-2.5 bg-[#111010] border border-[#504A4A] rounded-lg text-[#EBFFEE] placeholder-[#A9A3A3] text-sm focus:outline-none focus:border-green-700"
                  />
                  <Button
                    size="sm"
                    className="bg-green-700 hover:bg-green-800 text-white shrink-0"
                    onClick={() => {
                      const price = parseFloat(customItemPrice.replace(/[^0-9.]/g, ''));
                      handleAddMiscItem(customItemName, price);
                    }}
                  >
                    Add
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-[#A9A3A3] hover:text-[#EBFFEE] shrink-0"
                    onClick={() => {
                      setShowCustomPriceInput(false);
                      setCustomItemName('');
                      setCustomItemPrice('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Cart */}
            <div className="border border-[#504A4A] rounded-lg bg-[#221F1F] overflow-hidden">
              {cartItems.length > 0 ? (
                cartItems.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center gap-3 p-3 border-b border-[#504A4A]/50 last:border-b-0"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#EBFFEE] truncate">
                        {item.label}
                      </p>
                      <p className="text-sm text-[#A9A3A3]">
                        ${item.amount.toFixed(2)} × {item.quantity}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        className="w-8 h-8 rounded-lg bg-[#363232] flex items-center justify-center text-[#EBFFEE] hover:bg-[#504A4A] disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => handleQuantityChange(item, -1)}
                        disabled={item.quantity <= 1}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      <span className="w-8 text-center text-sm text-[#EBFFEE]">
                        {item.quantity}
                      </span>
                      <button
                        type="button"
                        className="w-8 h-8 rounded-lg bg-[#363232] flex items-center justify-center text-[#EBFFEE] hover:bg-[#504A4A]"
                        onClick={() => handleQuantityChange(item, 1)}
                      >
                        <ChevronUp className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-sm font-medium text-[#EBFFEE] w-14 text-right">
                      ${(item.amount * item.quantity).toFixed(2)}
                    </p>
                    <button
                      type="button"
                      className="p-2 text-[#A9A3A3] hover:text-red-500 hover:bg-red-500/10 rounded-lg"
                      onClick={() => handleRemoveCartItem(item)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))
              ) : (
                <div className="px-4 py-6 text-center text-sm text-[#A9A3A3]">
                  {selectedFamily
                    ? 'Check balance items above or add items'
                    : customerType === 'member'
                      ? 'Select a member to pay balance, or add items above'
                      : 'Add items above (guest payments coming soon)'}
                </div>
              )}
            </div>

            {/* Discount Code */}
            <div className="mt-4">
              <label className="block text-xs text-[#A9A3A3] mb-2">
                Discount Code (coming soon)
              </label>
              <div className="relative">
                <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A9A3A3] pointer-events-none" />
                <select
                  className="w-full pl-10 pr-10 py-2.5 bg-[#221F1F] border border-[#504A4A] rounded-lg text-[#A9A3A3] text-sm focus:outline-none focus:border-green-700 appearance-none cursor-not-allowed"
                  disabled
                  defaultValue=""
                >
                  <option value="" className="bg-[#221F1F] text-[#A9A3A3]">
                    Select discount code
                  </option>
                  {discountCodes
                    .filter((dc) => dc.is_active)
                    .map((dc) => (
                      <option
                        key={dc.id}
                        value={dc.id}
                        className="bg-[#221F1F] text-[#EBFFEE]"
                      >
                        {dc.code} — {dc.discount_type === 'percent' ? `${dc.discount_value}%` : `$${dc.discount_value}`}
                      </option>
                    ))}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[#A9A3A3]">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Total Due - pinned within section */}
            <div className="mt-4 p-4 rounded-lg bg-[#363232] border border-[#504A4A]">
              <p className="text-sm text-[#A9A3A3] mb-1">Total Due</p>
              <p className="text-2xl font-bold text-[#EBFFEE]">${totalDue.toFixed(2)}</p>
            </div>
          </section>

          {/* SECTION 3: Payment Method */}
          <section>
            <h3 className="text-sm font-medium text-[#A9A3A3] mb-3">Payment Method</h3>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => canUseCard && setPaymentMethod('card')}
                className={`flex-1 flex flex-col items-center gap-2 py-4 rounded-lg border-2 transition-colors ${
                  paymentMethod === 'card'
                    ? 'border-green-700 bg-green-700/10'
                    : canUseCard
                      ? 'border-[#504A4A] bg-[#221F1F] hover:border-[#504A4A]/80 cursor-pointer'
                      : 'border-[#504A4A] bg-[#221F1F] opacity-60 cursor-not-allowed'
                }`}
                title={canUseCard ? undefined : 'Select exactly one balance item to pay by card'}
              >
                <CreditCard className="w-8 h-8 text-[#EBFFEE]" />
                <span className="text-sm font-medium text-[#EBFFEE]">Card</span>
                {!canUseCard && (
                  <span className="text-xs text-[#A9A3A3]">One item only</span>
                )}
              </button>
              <button
                type="button"
                onClick={() => setPaymentMethod('cash')}
                className={`flex-1 flex flex-col items-center gap-2 py-4 rounded-lg border-2 transition-colors ${
                  paymentMethod === 'cash'
                    ? 'border-green-700 bg-green-700/10'
                    : 'border-[#504A4A] bg-[#221F1F] hover:border-[#504A4A]/80'
                }`}
              >
                <Banknote className="w-8 h-8 text-[#EBFFEE]" />
                <span className="text-sm font-medium text-[#EBFFEE]">Cash</span>
              </button>
              <button
                type="button"
                onClick={() => setPaymentMethod('check')}
                className={`flex-1 flex flex-col items-center gap-2 py-4 rounded-lg border-2 transition-colors ${
                  paymentMethod === 'check'
                    ? 'border-green-700 bg-green-700/10'
                    : 'border-[#504A4A] bg-[#221F1F] hover:border-[#504A4A]/80'
                }`}
              >
                <CheckSquare className="w-8 h-8 text-[#EBFFEE]" />
                <span className="text-sm font-medium text-[#EBFFEE]">Check</span>
              </button>
            </div>

            {paymentMethod === 'card' && (
              <div className="mt-4">
                {!canUseCard ? (
                  <div className="p-4 rounded-lg bg-[#363232] border border-[#504A4A] text-center">
                    <p className="text-sm text-[#A9A3A3]">
                      Select exactly one balance item to pay by card. For multiple items, use Cash or Check.
                    </p>
                  </div>
                ) : createPaymentIntentMutation.isError ? (
                  <div className="p-4 rounded-lg bg-[#363232] border border-[#504A4A] space-y-2">
                    <p className="text-sm text-red-400">
                      {createPaymentIntentMutation.error?.message}
                    </p>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-[#A9A3A3] hover:text-[#EBFFEE]"
                      onClick={() => createPaymentIntentMutation.mutate({ enrollmentId: singleEnrollmentId! })}
                    >
                      Try again
                    </Button>
                  </div>
                ) : createPaymentIntentMutation.isPending || !createPaymentIntentMutation.data?.client_secret ? (
                  <div className="p-4 rounded-lg bg-[#363232] border border-[#504A4A] flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin text-[#A9A3A3]" />
                    <span className="text-sm text-[#A9A3A3]">Preparing payment form…</span>
                  </div>
                ) : stripePromise && createPaymentIntentMutation.data?.client_secret ? (
                  <div className="p-4 rounded-lg bg-[#363232] border border-[#504A4A]">
                    <Elements
                      stripe={stripePromise}
                      options={{
                        clientSecret: createPaymentIntentMutation.data.client_secret,
                        appearance: {
                          theme: 'night',
                          variables: { colorPrimary: '#15803d', colorBackground: '#111010' },
                        },
                      }}
                    >
                      <CardPaymentForm
                        onSuccess={() => {
                          showToast('Payment successful', 'success');
                          queryClient.invalidateQueries({ queryKey: ['subscriptionsWithAmountDue'] });
                          queryClient.invalidateQueries({ queryKey: ['payment-records'] });
                          queryClient.invalidateQueries({ queryKey: ['payments'] });
                          resetForm();
                          setShowSuccess(true);
                        }}
                        onError={(msg) => showToast(msg, 'error')}
                      />
                    </Elements>
                  </div>
                ) : null}
              </div>
            )}

            {(paymentMethod === 'cash' || paymentMethod === 'check') && (
              <div className="mt-4 space-y-3">
                <div>
                  <label className="block text-xs text-[#A9A3A3] mb-2">
                    Amount Received
                  </label>
                  <input
                    type="text"
                    placeholder="0.00"
                    value={amountReceived}
                    onChange={(e) => setAmountReceived(e.target.value)}
                    className="w-full px-4 py-2.5 bg-[#221F1F] border border-[#504A4A] rounded-lg text-[#EBFFEE] placeholder-[#A9A3A3] text-lg focus:outline-none focus:border-green-700"
                  />
                </div>
                <div className="p-4 rounded-lg bg-[#363232] border border-[#504A4A]">
                  <p className="text-xs text-[#A9A3A3] mb-1">Change to Return</p>
                  <p className="text-2xl font-bold text-[#EBFFEE]">
                    ${changeDue.toFixed(2)}
                  </p>
                </div>
              </div>
            )}
          </section>
        </div>

        {/* SECTION 4: Action - bottom pinned (hidden when card form is shown) */}
        {!(paymentMethod === 'card' && canUseCard && createPaymentIntentMutation.data?.client_secret) && (
        <div className="p-4 border-t border-[#504A4A] bg-[#1a1818] shrink-0">
          <Button
            className="w-full py-3 bg-green-700 hover:bg-green-800 text-white text-base font-semibold"
            disabled={
              recordPaymentMutation.isPending ||
              (customerType === 'member' && (cartItems.length === 0 || totalDue <= 0)) ||
              (customerType === 'member' &&
                (paymentMethod === 'cash' || paymentMethod === 'check') &&
                amountNum <= 0) ||
              (customerType === 'guest' && cartItems.length === 0)
            }
            onClick={async () => {
              if (customerType === 'guest') {
                showToast('Guest purchases coming soon', 'info');
                return;
              }
              if (customerType === 'member') {
                if (cartItems.length === 0 || totalDue <= 0) {
                  showToast('Check the balance items you are paying or add items', 'error');
                  return;
                }
                if (hasAddedItems) {
                  showToast(
                    'Paying for miscellaneous items is coming soon. Remove them to pay balance only.',
                    'info',
                  );
                  return;
                }
                if (amountNum <= 0) {
                  showToast('Enter amount received', 'error');
                  return;
                }
                if (!selectedFamily) return;

                const paymentAmount = Math.min(amountNum, totalDue);
                const balanceItems = cartItems.filter((i): i is CartItem & { enrollmentId: string } =>
                  !!i.enrollmentId,
                );
                const totalOutstanding = balanceItems.reduce(
                  (sum, i) => sum + i.amount * i.quantity,
                  0,
                );

                try {
                  const promises = balanceItems.map((item) => {
                    const proportion = (item.amount * item.quantity) / totalOutstanding;
                    const subPayment = Math.min(
                      paymentAmount * proportion,
                      item.amount * item.quantity,
                    );
                    if (subPayment <= 0) return Promise.resolve();
                    return recordPaymentMutation.mutateAsync({
                      enrollmentId: item.enrollmentId,
                      amount: Math.round(subPayment * 100) / 100,
                      paymentMethod: paymentMethod as 'cash' | 'check',
                      billingDate: item.billingDate,
                      notes: `Add Payment (${selectedFamily.family_lead_last_name})`,
                    });
                  });
                  await Promise.all(promises);
                  showToast(`Payment of $${paymentAmount.toFixed(2)} recorded`, 'success');
                  resetForm();
                  setShowSuccess(true);
                } catch (err) {
                  showToast(
                    err instanceof Error ? err.message : 'Failed to record payment',
                    'error',
                  );
                }
              }
            }}
          >
            {recordPaymentMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              'Complete Payment'
            )}
          </Button>
        </div>
        )}
        </div>
      </div>

      {/* Success Overlay */}
      {showSuccess && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4">
          <div className="w-full max-w-sm bg-[#221F1F] border border-[#504A4A] rounded-xl p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-700/20 flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-700" />
            </div>
            <h3 className="text-xl font-semibold text-[#EBFFEE] mb-2">
              Payment Complete
            </h3>
            <p className="text-sm text-[#A9A3A3] mb-6">
              Receipt has been generated
            </p>
            <div className="space-y-3">
              <Button
                className="w-full bg-green-700 hover:bg-green-800 text-white"
                onClick={() => {
                  /* TODO: Email receipt */
                }}
              >
                <Mail className="w-4 h-4 mr-2 inline" />
                Email Receipt
              </Button>
              <Button
                variant="ghost"
                className="w-full text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-white/5"
                onClick={() => {
                  setShowSuccess(false);
                  onClose();
                }}
              >
                Done
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
